import { useMemo } from 'react'
import { createClient } from '@connectrpc/connect'
import { useTransport } from '@connectrpc/connect-query'
import { useInfiniteQuery } from '@tanstack/react-query'

import {
  Order,
  WellKnownCollection,
} from '@jotta/grpc-connect-openapi/collection'
import type { FieldMask } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/mask_pb'
import { CollectionService } from '@jotta/grpc-connect-openapi/esm/no/jotta/openapi/photo/v2/collection.v2_pb'
import type {
  Photo_PhotoAction,
  PhotoRef,
} from '@jotta/grpc-connect-openapi/photo'
import { queryClient } from '@jotta/query'
import type { PartialMessage } from '@jotta/types/compat'
import type { satisfy } from '@jotta/utils/typeAssertions'

import type { CollectionRef } from '../../../api/useCollection'
import type { SelectablePhoto } from '../../../store/SelectionStore'

import { fromTimestampNanos } from './util'

/**
 * The photo fields every Timeline v2 list asks for, so a selection made from it carries
 * what the selection bar needs to show and run its actions without fetching each photo
 * (see `useGatePhotos`). A field left out of a mask still arrives, as its default value:
 * without `actions` every photo would read as allowing nothing (`[]`), so the bar would
 * offer only Download.
 */
export const selectablePhotoFields = [
  { fieldNumber: 1 }, // idDeprecated
  { fieldNumber: 2 }, // md5: Share and Add to album address photos by it
  { fieldNumber: 4 }, // ref
  { fieldNumber: 6 }, // timestampNanos
  { fieldNumber: 7 }, // videoDurationSeconds
  { fieldNumber: 19 }, // encodedContentRef
  { fieldNumber: 21 }, // isLivePhoto
  { fieldNumber: 23 }, // actions
]

/** Timeline rows: what a selection needs, plus what the grid draws. */
export const timelinePhotoMask: PartialMessage<FieldMask> = {
  includes: [
    {
      fieldNumber: 1, // photo
      childMask: {
        includes: [
          ...selectablePhotoFields,
          { fieldNumber: 5 }, // expiringSmallThumbUrl
          { fieldNumber: 8 }, // filesize
          { fieldNumber: 9 }, // filename
          { fieldNumber: 10 }, // Width
          { fieldNumber: 11 }, // Height
          { fieldNumber: 22 }, // thumbHash
        ],
      },
    },
  ],
}

export type TimelinePhoto = satisfy<
  SelectablePhoto,
  {
    id: string
    idDeprecated: string
    md5: string
    ref: PhotoRef | undefined
    expiringSmallThumbUrl: string
    timestampNanos: bigint
    videoDurationSeconds?: number
    isLivePhoto?: boolean
    fileSize: bigint
    fileName: string
    width: number
    height: number
    encodedContentRef: string
    thumbHash?: Uint8Array
    actions: Photo_PhotoAction[]
  }
>

export function usePhotoCollectionService() {
  const transport = useTransport()
  const service = useMemo(
    () => createClient(CollectionService, transport),
    [transport],
  )

  return service
}

export const timelineCollection = WellKnownCollection.TIMELINE
export const hiddenTimelineCollection = WellKnownCollection.HIDDEN

export type TimelineType =
  | typeof WellKnownCollection.TIMELINE
  | typeof WellKnownCollection.HIDDEN
function asCollectionRef(type: TimelineType): CollectionRef {
  return {
    ref: {
      case: 'wellKnownCollection',
      value: type,
    },
  }
}
export function useCollectionRef(type: TimelineType) {
  return useMemo(() => asCollectionRef(type), [type])
}
export const timelineRef = asCollectionRef(timelineCollection)

export const hiddenTimelineRef = asCollectionRef(hiddenTimelineCollection)

const initialLimit = 100
const dayLimit = 900

export type TimelinePhotoPage = {
  photos: TimelinePhoto[]
  incompleteDaySplit?: boolean
}

// during infinite query validation, tanstack might flip the direction to
// refetch a page in the opposite direction of what we set in the page parameters.
// this can prevent the invalidation from being able to load anything since the page
// in question can be the last page.
//
// therefore, we flip the order if we detect that the direction was flipped
function resolveOrder(order: Order, direction: 'forward' | 'backward') {
  if (order === Order.DESC && direction === 'forward') {
    return order
  }
  if (order === Order.ASC && direction === 'backward') {
    return order
  }
  return order === Order.DESC ? Order.ASC : Order.DESC
}

export function useTimelineQuery(
  collectionRef: CollectionRef,
  timestamp: bigint,
) {
  const service = usePhotoCollectionService()
  return useInfiniteQuery({
    queryKey: [
      CollectionService.typeName,
      CollectionService.method.getCollectionPhotosMasked.name,
      collectionRef,
      timestamp.toString(),
    ],
    // prevents a cached, empty page signaling the end of the timeline in this direction
    // from being used in the opposite direction which incorrectly will end paging this way as well
    gcTime: 0,
    initialPageParam: {
      timestamp,
      order: Order.DESC,
    },
    getPreviousPageParam: ({ photos }: TimelinePhotoPage) => {
      const earliestPhoto = photos.at(0)
      return (
        earliestPhoto && {
          timestamp: earliestPhoto.timestampNanos + 1n,
          order: Order.ASC,
        }
      )
    },
    getNextPageParam: ({ photos }: TimelinePhotoPage) => {
      const oldestPhoto = photos.at(-1)
      return (
        oldestPhoto && {
          timestamp: oldestPhoto.timestampNanos - 1n,
          order: Order.DESC,
        }
      )
    },
    queryFn: async ({ pageParam, signal, direction }) => {
      const { order: orderParam, timestamp } = pageParam
      const order = resolveOrder(orderParam, direction)

      const photos: TimelinePhoto[] = []

      const initialResponse = service.getCollectionPhotosMasked(
        {
          collectionRef,
          limit: initialLimit,
          mask: timelinePhotoMask,
          selection: {
            case: 'scan',
            value: {
              order,
              start: {
                case: 'startInclusiveTimestampNanos',
                value: timestamp,
              },
            },
          },
        },
        {
          signal,
        },
      )

      for await (const { photo } of initialResponse) {
        if (!photo) {
          continue
        }
        photos.push({
          ...photo,
          id: photo.idDeprecated,
          ref: photo.ref,
        })
      }

      // after an initial limited fetch, we want to fetch any remaining
      // photos of the last day to avoid re-flowing when we lay them out
      // in reversed chronological order
      if (!photos.length || order !== Order.ASC) {
        return {
          photos,
        }
      }
      const initialPhotoCount = photos.length
      const startExclusiveTimestampNanos =
        photos[photos.length - 1].timestampNanos
      const stopExclusiveTimestampNanos =
        BigInt(
          fromTimestampNanos(startExclusiveTimestampNanos)
            .endOf('day')
            .valueOf(),
        ) * 1_000_000n

      const restOfDayResponse = service.getCollectionPhotosMasked(
        {
          collectionRef,
          mask: timelinePhotoMask,
          limit: dayLimit,
          selection: {
            case: 'range',
            value: {
              startExclusiveTimestampNanos,
              stopExclusiveTimestampNanos,
            },
          },
        },
        {
          signal,
          timeoutMs: 20_000,
        },
      )

      for await (const { photo } of restOfDayResponse) {
        if (!photo) {
          continue
        }
        photos.push({
          ...photo,
          id: photo.idDeprecated,
          ref: photo.ref,
        })
      }
      // Turn ASC into DESC so the infinite data can be viewed sequentially
      photos.reverse()

      // If we hit the limit we assume the day is incomplete and needs splitting
      if (photos.length - initialPhotoCount === dayLimit) {
        photos.splice(photos.length - dayLimit / 4)
        return {
          photos,
          incompleteDaySplit: true,
        }
      }

      return {
        photos,
      }
    },
  })
}

export const invalidateQueries = () =>
  queryClient.invalidateQueries({
    queryKey: [CollectionService.typeName],
  })
